import { useState, useEffect, useRef, type ReactNode } from 'react';
import type { InteractionRequest } from '@arcanum-hq/shared-types';
import { InteractionSurface } from '@/components/builder/interaction';

const PAGE_BADGES: Record<string, string> = {
  '/': 'COCKPIT',
  '/solver': 'SOLVER',
  '/builder': 'BUILDER',
  '/pipeline': 'PIPELINE',
  '/settings': 'SETTINGS',
  '/deal': 'SHARED DEAL',
};

const PAGE_SUGGESTIONS: Record<string, string[]> = {
  '/': [
    'Run 96-unit ATL deal at $8.2M, $1,400 rents?',
    "What's my best deal this month?",
  ],
  '/solver': [
    'Solve for max purchase price at 15% IRR',
    'What if rates drop 50 bps by close?',
  ],
  '/pipeline': [
    'Which deal is most sensitive to rent miss?',
    'Show me deals above 75 signal score',
  ],
  '/builder': ['Run sensitivity on exit cap rate', 'Compare to Nashville deal'],
  '/settings': [],
};

const PAGE_PLACEHOLDERS: Record<string, string> = {
  '/': '"92 units, Buckhead, $185K/door, rents at $1,850..."',
  '/solver': '"Can I make this work at 15% IRR if I cap reno at $18K?"',
  '/pipeline': '"Compare my two Atlanta deals side by side..."',
  '/builder': 'Describe your deal or add more details...',
  '/settings': 'Ask a question...',
};

const PROMPT_PLACEHOLDER = 'Ask about this deal…';

// Suggestion-chip class per variant (public-deal-link-polish WS2). FULL static
// literals — Tailwind v4 has NO safelist, so an interpolated `border-${x}`
// class is silently never emitted. `default` = dim panel-border (auth surfaces);
// `high-contrast` = accent-tinted (public deal link, the primary on-ramp).
const CHIP_VARIANT_CLASS: Record<'default' | 'high-contrast', string> = {
  default:
    'px-2.5 py-1 border border-panel-border rounded-[2px] text-[11px] font-mono text-text-bright hover:border-accent/40 hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-panel-border disabled:hover:text-text-bright',
  'high-contrast':
    'px-2.5 py-1 border border-accent/40 bg-accent/10 rounded-[2px] text-[11px] font-mono text-accent hover:bg-accent/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent/10',
};

export type NarrativeBarMode = 'full' | 'prompt';

/**
 * `external-sharing` Phase 5 Session 5.2 (2026-05-09).
 *
 * Discriminator that drives gate-firing semantics on shared-deal
 * surfaces. Consumers compute the value via
 * `resolveShareMode(viewerKind, isAuthed)` (lives in `routes/deal.tsx`
 * for the public surface) or pin to `'internal-recipient'` for the
 * `<ReadOnlyShareBuilder>` integration on `/builder?share=<id>`.
 *
 *   - `'external-anon'`: anonymous viewer on `/deal/:shareId`. Gate
 *     fires per `maxQuestions` + `gateContent` (the existing Phase 6
 *     behavior; consumer typically passes `<UnlockFullArcanumAccessPrompt>`).
 *   - `'external-authed'`: signed-in viewer who is NOT the share's
 *     recipient. Gate STILL fires; consumer passes
 *     `<AuthedPostGatePrompt>` as `gateContent` (rather than the
 *     Sign-Up CTA, which is irrelevant for already-authenticated users).
 *   - `'internal-recipient'`: signed-in viewer who IS the share's
 *     recipient. NEVER gates regardless of `questionCount` — they're
 *     the intended audience.
 *   - `'owner'`: signed-in viewer who is the share's owner. NEVER
 *     gates (used by Session 5.4's `?preview=true` bypass on
 *     `/deal/:shareId`; the regular owner-redirect short-circuit at
 *     `routes/deal.tsx:79-86` means this branch is unreachable in v1
 *     without the bypass).
 *
 * Absence (undefined) preserves the pre-Phase-5 behavior — the bar
 * gates iff `maxQuestions` + `gateContent` are set, regardless of
 * viewer kind. Existing consumers (Builder Workbench/Signal/Model
 * NarrativeBar mounts) keep working unchanged.
 */
export type NarrativeBarShareMode =
  | 'external-anon'
  | 'external-authed'
  | 'internal-recipient'
  | 'owner';

export interface NarrativeBarProps {
  /**
   * Optional pathname — drives the page badge + suggestion list +
   * placeholder copy in `mode="full"`. Defaults to the current location
   * when omitted (rare; consumers usually pass it explicitly so the bar
   * doesn't read from the router synchronously).
   */
  pathname?: string;
  /**
   * `'full'` (default) renders the existing 2-row bar with badge,
   * suggestion chips, and an input. `'prompt'` collapses to a slim
   * single-row "Ask about this deal" composer with no suggestion chips —
   * used on the Builder Signal/Model tabs and on the public deal page
   * where the workbench's chat surface doesn't apply.
   *
   * Configuration over conditionals: NEW per-route consumers should
   * pass `mode` explicitly rather than letting the bar guess from
   * `pathname`. SHIPPED `agent-ux-optimization` Phase 6 Session 7
   * (2026-05-08).
   */
  mode?: NarrativeBarMode;
  /**
   * Soft 3-question gate ceiling. When `questionCount >= maxQuestions`,
   * the bar swaps its body for `gateContent`. Used exclusively by the
   * public deal page today (`maxQuestions={3}`); authenticated routes
   * leave this undefined → no gate.
   */
  maxQuestions?: number;
  /**
   * Rendered in place of the bar body when `gated === true`. Typically a
   * conversion CTA like `<UnlockFullArcanumAccessPrompt>`. The bar still
   * occupies its layout slot (the outer wrapper renders) so the
   * surrounding chrome height stays stable across the gate flip.
   */
  gateContent?: ReactNode;
  /**
   * Optional async submit hook — fires on Enter or send-button click.
   * Returns true to mark the question accepted (count increments,
   * input clears); false to keep the input populated. Public-deal
   * consumers will wire this to `useRecordQuestion` in a future
   * Session 8 follow-up; today it's optional and synchronous.
   */
  onSubmit?: (input: string) => Promise<boolean> | boolean;
  /**
   * Shared-deal surface discriminator — see `NarrativeBarShareMode`
   * for the mode matrix. When set to `'internal-recipient'` or
   * `'owner'`, the gate is suppressed entirely (the bar renders
   * normally regardless of `questionCount`). The other two modes
   * keep the existing gate-firing behavior; consumer chooses the
   * appropriate `gateContent` per mode.
   */
  shareMode?: NarrativeBarShareMode;
  /**
   * `deal-signal-overhaul` Phase 5 Session 4 (2026-05-13). Seed value
   * for the local `questionCount` state. When set, `useState(initialQuestionCount ?? 0)`
   * initializes the counter so the gate fires at the correct threshold
   * on mount (e.g. a viewer who's already asked 2 of 3 questions in a
   * prior session arrives at count=2 — the next accepted submit flips
   * to 3 and the gate fires immediately).
   *
   * **Uncontrolled w.r.t. server state.** This prop is a SEED only —
   * subsequent prop changes do NOT re-sync the local counter. The
   * refresh-mode rehydration works because `usePublicDealConversation`
   * re-fetches on mount, feeding a fresh seed value through this prop
   * on the next render cycle (after the component remounts following
   * the route's hydration settle). Mid-conversation invalidations of
   * the conversation query do NOT propagate into the bar's count.
   *
   * Auth consumers (`routes/builder.tsx`) leave this undefined —
   * they have no persistent server-side counter and the local
   * `useState(0)` default is the canonical seed.
   */
  initialQuestionCount?: number;
  /**
   * `deal-signal-overhaul` Phase 5 click-to-expand (2026-05-13).
   * Optional list of clickable suggestion chips rendered ABOVE the
   * textarea in `mode='prompt'`'s EXPANDED state. Slim/default state
   * does not render chips — the right-side chevron expands the bar
   * to reveal them. When undefined/empty, the expanded state shows
   * only the (taller) textarea + send button. Consumers wire this
   * to `usePublicDeal(shareId).data.suggestionChips.chips`.
   */
  expandedSuggestions?: string[];
  /**
   * Fires when an expanded-state suggestion chip is clicked. The
   * chip's text IS the message — no composer-prefill round-trip. The
   * wrapper's `sendGuarded(content)` should ignore the bar's
   * `onSubmit` return value contract here (chip clicks are fire-and-
   * forget; the textarea is not the source). If absent, chips render
   * as inert text.
   */
  onSuggestionClick?: (chip: string) => void;
  /**
   * Chip visual variant (public-deal-link-polish WS2). `'default'` is the dim
   * panel-border treatment used on the auth surfaces; `'high-contrast'` is the
   * accent-tinted variant for the public deal link (`border-accent/40
   * bg-accent/10 text-accent`) so the starter questions read as the primary
   * on-ramp. Static-literal class lookup (Tailwind v4 has NO safelist — an
   * interpolated `border-${x}` would be silently dropped).
   */
  chipVariant?: 'default' | 'high-contrast';
  /**
   * `deal-signal-overhaul` Phase 5 redesign (2026-05-12). External
   * disable signal — the input/textarea + send button + suggestion
   * chips all read as `disabled`; submits are no-ops. Replaces the
   * bar-internal `gateContent` swap for the public-deal surface
   * (the gate now renders as an in-thread system message inside
   * `<PublicDealChatThread>`; the composer stays in place but
   * accepts no input). Public-deal consumer passes
   * `disabled={conversation.data?.gateActive ?? false}` so the
   * composer disables when the 3-question gate fires.
   */
  disabled?: boolean;
  /**
   * Placeholder shown when `disabled` is true. Falls back to the
   * regular placeholder if omitted. Public-deal consumer passes
   * `"Sign up to continue…"` so the user immediately sees why the
   * composer no longer accepts input.
   */
  disabledPlaceholder?: string;
  /**
   * `deal-signal-overhaul` Phase 5 redesign (2026-05-12). Optional
   * render-prop invoked with the bar's current `expanded` flag. When
   * provided, the bar renders the returned content ABOVE the composer
   * row (sibling-composition, NOT inside the composer wrapper) — used
   * by the internal Signal tab to surface the Workbench `<ChatThread>`
   * inside the expand affordance without minting a second
   * `useStreaming()` hook. Caller is responsible for any clipping
   * height / scroll container. Pass-through is intentional: the bar
   * doesn't mount the content; it just hands the consumer back the
   * expanded flag. Omit to retain pre-redesign behavior.
   */
  renderAboveComposer?: (expanded: boolean) => ReactNode;
  /**
   * `deal-signal-overhaul` Phase 5 Lovable-fidelity redesign
   * (2026-05-12). Optional override for the page-tag pill in the
   * header (e.g. `"SIGNAL"`, `"SHARED DEAL"`). Defaults to the
   * pathname-derived `PAGE_BADGES` lookup. Consumer override is
   * preferred for surfaces that don't naturally derive from pathname
   * (Builder Signal tab, public deal).
   */
  headerBadge?: string;
  /**
   * Subtitle next to the header badge — typically the deal title.
   * Renders as `· {dealName}` in muted type. Omit for surfaces with
   * no deal context (e.g. cockpit's full-mode bar).
   */
  dealName?: string;
  /**
   * Public-deal free-question budget (F-UX-07, 2026-06-04). When set
   * (prompt mode), the header renders an always-visible
   * `{questionCount} of {questionBudget} free questions` scarcity chip —
   * COLLAPSED and expanded — so the prospect sees the budget at the exact
   * moment it would motivate sign-up (previously the count only appeared
   * once the overlay was expanded). Display-only; the gate is enforced
   * server-side. Omit on auth surfaces (no free-question budget there).
   */
  questionBudget?: number;
  /**
   * `table-interaction-surface` (2026-06-09). When set, the `mode='prompt'`
   * slim composer body is REPLACED by `<InteractionSurface density="narrative">`.
   * Distinct from `renderAboveComposer` (sibling-overlay-above, requires
   * expand) — this replaces the composer row itself. Restoring to the chat
   * composer is the caller's responsibility via `onInteractionDismiss`.
   * Undefined on all pre-existing consumers — additive, no behavioral change.
   */
  interaction?: InteractionRequest;
  /**
   * Called when the interaction surface's dismiss button (✕) is clicked.
   * Host (routes/builder.tsx) restores `composerMode='chat'` and clears
   * `activeInteraction`. Must be provided when `interaction` is set.
   */
  onInteractionDismiss?: () => void;
  /**
   * Forwarded to `<InteractionSurface currentInputs>` — the views.inputs map
   * (`Record<namedRange, { value, source }>`) from `useConversationViews`.
   * Seeds the `<TableGridEditor>` with the current model values. Optional —
   * the grid falls back to schema defaults when absent.
   */
  currentInputs?: Record<string, { value: unknown; source?: string }>;
  /**
   * Billing read-only gate forwarded to `<InteractionSurface>`. Disables the
   * Submit button when the user's monthly allowance is exhausted.
   */
  interactionBillingReadOnly?: boolean;
  /**
   * market-default-apply-ux Phase 2 — forwarded to `<InteractionSurface
   * eligibleInputs>`. Required for the `market_default_apply` arm's lazy
   * suggestion hook (the full `{name,displayName,dataType,description?}` set
   * the server's Haiku prompt needs). Without it, an arm opened from the
   * Signal/Model tab (which hosts the InteractionSurface HERE, not in
   * `<ChatThread>`) would post `eligibleInputs:[]` → zero AI suggestions.
   */
  eligibleInputs?: Array<{
    name: string;
    displayName: string;
    dataType: string;
    description?: string;
  }>;
}

/**
 * Bottom-of-page Narrative bar. Per-route mounted as of `agent-ux-optimization`
 * Phase 6 Session 7 (2026-05-08); pre-Session-7 it lived as a single
 * `__root.tsx` mount that surfaced on every authenticated page. Now it
 * surfaces ONLY on Builder, Solver, and the public deal link.
 *
 * Three render branches:
 *   1. Gated (`mode='full' | 'prompt'`, `gateContent` supplied,
 *      `questionCount >= maxQuestions`) → render `gateContent`.
 *   2. `mode='full'` → 2-row bar with badge + suggestions + input.
 *   3. `mode='prompt'` → slim single-row composer with no suggestions.
 */
export function NarrativeBar({
  pathname = '/',
  mode = 'full',
  maxQuestions,
  gateContent,
  onSubmit,
  shareMode,
  initialQuestionCount,
  expandedSuggestions,
  onSuggestionClick,
  chipVariant = 'default',
  disabled = false,
  disabledPlaceholder,
  renderAboveComposer,
  headerBadge,
  dealName,
  questionBudget,
  interaction,
  onInteractionDismiss,
  currentInputs,
  interactionBillingReadOnly = false,
  eligibleInputs,
}: NarrativeBarProps) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const [questionCount, setQuestionCount] = useState(initialQuestionCount ?? 0);
  // `deal-signal-overhaul` Phase 5 click-to-expand (2026-05-13).
  // `prompt`-mode bar starts slim; right-side chevron toggles the expanded
  // state which reveals suggestion chips above a taller textarea + send.
  // No persistence across mounts (intentionally — match other in-page
  // expand affordances). Default false.
  const [expanded, setExpanded] = useState(false);

  // WS4: the public-deal header "Ask the AI" CTA dispatches
  // `arcanum:open-narrative-bar`; this listener expands + focuses the composer
  // so the visitor lands in the chat. Plain addEventListener + cleanup is
  // strict-mode-safe (no one-shot dispatch-then-consume dedup needed — the
  // event is fire-on-click, not URL-driven). The event is global; only the
  // public deal route mounts a prompt-mode bar, so there's no cross-surface
  // fire. Defined ABOVE the gated early-return to keep hook order stable.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onOpen = () => {
      setExpanded(true);
      // Defer focus until after the expand re-render paints the composer.
      window.setTimeout(() => inputRef.current?.focus(), 0);
    };
    window.addEventListener('arcanum:open-narrative-bar', onOpen);
    return () =>
      window.removeEventListener('arcanum:open-narrative-bar', onOpen);
  }, []);

  // Auto-scroll the expanded overlay to the LATEST message (2026-06-04). When
  // the bar opens with an existing conversation, the overlay must land at the
  // bottom so the newest turn (and the optimistic human bubble + streaming
  // reply) is visible — not scrolled to the top of a long thread, and it stays
  // pinned to the bottom as tokens stream in / new turns persist.
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!expanded) return;
    const el = overlayRef.current;
    if (!el) return;
    const toBottom = () => {
      el.scrollTop = el.scrollHeight;
    };
    // `pinned` tracks whether the viewer is parked at the bottom. It flips ONLY
    // on a user scroll — content appended below (a new bubble, streamed tokens)
    // must NOT flip it, or a single large append would read as a scroll-up and
    // auto-follow would stop (the streamed answer then grows out of view). So
    // the scroll listener owns `pinned`; the MutationObserver just re-pins to
    // the bottom whenever `pinned` is still true, letting a manual scroll-up to
    // re-read earlier answers cleanly opt out of auto-follow.
    let pinned = true;
    const onScroll = () => {
      pinned = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Scroll to the bottom on open. A single rAF can fire before the overlay's
    // content (markdown tables, the property map slot) has finished laying out,
    // leaving scrollHeight stale and the scroll a no-op — so re-pin across a few
    // frames/timeouts to catch late layout. `pinned` stays true through these,
    // so they all land at the true bottom.
    const raf = requestAnimationFrame(toBottom);
    const timers = [
      window.setTimeout(toBottom, 80),
      window.setTimeout(toBottom, 220),
    ];
    const obs = new MutationObserver(() => {
      if (pinned) toBottom();
    });
    obs.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach((t) => window.clearTimeout(t));
      el.removeEventListener('scroll', onScroll);
      obs.disconnect();
    };
  }, [expanded]);

  // Internal recipients + owners never see the gate — they're the
  // intended audience (or the owner previewing what their recipient
  // sees). External-anon + external-authed continue to gate per the
  // existing maxQuestions + gateContent contract.
  const gateAllowed =
    shareMode !== 'internal-recipient' && shareMode !== 'owner';
  const gated =
    gateAllowed &&
    maxQuestions !== undefined &&
    questionCount >= maxQuestions &&
    !!gateContent;

  // Auto-expand the overlay so the user sees the reply stream in. Only
  // fires when the bar has an overlay slot to render (chat thread or
  // suggestion chips) — otherwise it'd be a no-op flip. Extracted so BOTH
  // the typed-submit path (handleSend) AND the suggestion-chip click path
  // (which bypasses handleSend — see onSuggestionClick wiring below) reveal
  // the conversation. Pre-2026-06-04 the gate lived ONLY in handleSend, so a
  // chip click fired the send but left the overlay collapsed — the streamed
  // answer (and the optimistic human bubble) were invisible until the user
  // manually clicked the chevron.
  function maybeExpandOverlay(): void {
    if (renderAboveComposer != null || (expandedSuggestions?.length ?? 0) > 0) {
      setExpanded(true);
    }
  }

  async function handleSend(): Promise<void> {
    if (disabled) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    const accepted = onSubmit ? await onSubmit(trimmed) : true;
    if (accepted) {
      setInput('');
      setQuestionCount((n) => n + 1);
      maybeExpandOverlay();
    }
  }

  const promptPlaceholder = disabled
    ? disabledPlaceholder ?? PROMPT_PLACEHOLDER
    : PROMPT_PLACEHOLDER;

  if (gated && gateContent) {
    return (
      <div
        data-testid="narrative-bar"
        data-mode={mode}
        data-gated="true"
        data-share-mode={shareMode}
        role="region"
        aria-label="Narrative bar (gated)"
        className="shrink-0 border-t border-panel-border bg-void"
      >
        <div className="px-6 py-3">{gateContent}</div>
      </div>
    );
  }

  if (mode === 'prompt') {
    // Overlay popover only carries `renderAboveComposer` now — suggestion
    // chips moved to the always-present slim-bar row (WS2), so they no longer
    // gate the overlay (otherwise suggestions-without-renderAboveComposer would
    // open an empty popover).
    const hasOverlayContent = expanded && renderAboveComposer != null;
    const isActive = focused || input.trim().length > 0;
    // The free-question budget chip must mirror the SERVER count (the same
    // value the in-thread counter + gate use), NOT the bar's uncontrolled
    // local `questionCount` state — chip-clicks bypass the local increment, so
    // the local count would lag (showing "2 of 3" while the gate already fired
    // at 3). The public-deal wrapper keeps `initialQuestionCount` synced to
    // `conversation.data.questionCount` on every render (it re-renders after the
    // post-`done` refetch), so reading the prop here keeps both displays in
    // lockstep. `??` (not `||`) so a legitimate 0 is preserved.
    const budgetQuestionCount = initialQuestionCount ?? questionCount;
    const headerBadgeText =
      headerBadge ??
      (pathname.startsWith('/deal/')
        ? 'SHARED DEAL'
        : PAGE_BADGES[pathname] ?? 'NARRATIVE');
    return (
      <div
        data-testid="narrative-bar"
        data-mode="prompt"
        data-expanded={expanded ? 'true' : 'false'}
        data-disabled={disabled ? 'true' : 'false'}
        data-question-count={questionCount}
        data-share-mode={shareMode}
        role="region"
        aria-label="Narrative bar"
        // `z-30` lifts the bar (and via its stacking context, the
        // `absolute bottom-full` overlay below) above the
        // `<PropertyMap>` controls which use `z-10` for the
        // zoom-in/zoom-out/recenter button cluster. Without this, the
        // map controls render through the expanded chat overlay. Surfaced
        // 2026-05-12 on the public deal-link page; same fix lands on
        // the auth-side Signal bar because both routes share this primitive.
        className="shrink-0 border-t border-panel-border bg-void relative z-30"
      >
        {/* `deal-signal-overhaul` Phase 5 overlay redesign (2026-05-12).
            When expanded, the bar's optional slot + suggestion chips
            float ABOVE the bar as a popover so signal content underneath
            stays in place. `absolute bottom-full` anchors the panel to
            the bar's top edge; the panel rises UPWARD and is clipped at
            70vh. Background is opaque so signal content doesn't show
            through. Collapsed state renders nothing — zero layout
            footprint until the user expands. */}
        {hasOverlayContent && (
          <div
            ref={overlayRef}
            data-testid="narrative-bar-overlay"
            className="scroll-accent absolute bottom-full left-0 right-0 max-h-[70vh] overflow-y-auto bg-void border-t border-panel-border shadow-[0_-12px_24px_-12px_rgba(0,0,0,0.5)]"
          >
            {/* Top-of-overlay collapse handle. Anchored to where the
                user's eye is when the overlay opens — gives a clear
                affordance to close the panel without scrolling back to
                the bar header. Sticky so it stays visible as the chat
                thread grows + scrolls. Accent green chevron pointing
                DOWN (down = collapse toward bar). 2026-05-12 — `expanding
                caret should be at the top of the expansion when expanded`. */}
            <button
              type="button"
              onClick={() => setExpanded(false)}
              data-testid="narrative-bar-collapse-top"
              aria-label="Collapse composer"
              aria-expanded
              // The expand/collapse caret, relocated to the TOP of the
              // conversation while expanded (it leaves the bottom header when
              // expanded). SOLID accent bar — opaque (so scrolled content
              // doesn't bleed through the sticky strip) + thick enough to read
              // as a clear handle; chevron-DOWN = "collapse toward the bar".
              // No green glow.
              className="sticky top-0 left-0 right-0 z-10 w-full flex items-center justify-center py-2.5 bg-accent text-void hover:bg-accent/90 transition-colors cursor-pointer"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 12 12"
                aria-hidden="true"
              >
                {/* chevron-down = collapse toward the bar */}
                <path
                  d="M2 4 L6 8 L10 4"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {renderAboveComposer && renderAboveComposer(expanded)}
            {/* Suggestion chips moved OUT of the expanded overlay → they now
                render in the always-present slim-bar row below the header
                (public-deal-link-polish WS2), so chips show collapsed too. */}
          </div>
        )}
        {/* === Row 1: header — NARRATIVE accent label + page badge pill +
            deal name + queries counter + chevron toggle. Lovable-fidelity
            density: 26-28px tall, 9-10px font-mono. */}
        <div
          data-testid="narrative-bar-header"
          className="flex items-center justify-between px-4 h-[28px] border-b border-panel-border"
        >
          <div className="flex items-center gap-2 min-w-0">
            {/* `‖` Logo glyph — twin accent bars. */}
            <svg
              width="9"
              height="11"
              viewBox="0 0 10 12"
              className="shrink-0"
              aria-hidden="true"
            >
              <rect
                x="0"
                y="0"
                width="3"
                height="12"
                rx="1.5"
                fill="var(--color-accent)"
              />
              <rect
                x="6"
                y="0"
                width="3"
                height="12"
                rx="1.5"
                fill="var(--color-accent)"
              />
            </svg>
            <span className="text-micro font-mono text-accent tracking-[1.4px] font-semibold">
              NARRATIVE
            </span>
            <span className="text-[8px] font-mono text-text-muted tracking-[0.5px] px-1.5 py-0.5 bg-accent-dim rounded-[2px]">
              {headerBadgeText}
            </span>
            {dealName && (
              <span className="text-[9px] font-mono text-text-dim truncate">
                · {dealName}
              </span>
            )}
            {questionBudget != null ? (
              <span
                data-testid="narrative-bar-question-budget"
                data-question-count={budgetQuestionCount}
                data-question-budget={questionBudget}
                className="text-[9px] font-mono text-accent tracking-[0.3px] px-1.5 py-0.5 bg-accent-dim border border-accent/30 rounded-[2px] tabular-nums"
              >
                {Math.min(budgetQuestionCount, questionBudget)} of{' '}
                {questionBudget} free questions
              </span>
            ) : (
              questionCount > 0 && (
                <span
                  data-testid="narrative-bar-counter"
                  className="text-[9px] font-mono text-text-dim tracking-[0.3px] px-1.5 py-0.5 bg-input-bg border border-panel-border rounded-[2px] tabular-nums"
                >
                  {questionCount} {questionCount === 1 ? 'query' : 'queries'}
                </span>
              )
            )}
          </div>
          {/* Expand caret — shown ONLY when collapsed. When expanded the caret
              "moves" to the top of the conversation overlay (the
              `narrative-bar-collapse-top` handle above), per the
              caret-at-the-top affordance. No green glow. */}
          {!expanded && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              data-testid="narrative-bar-toggle-expand"
              aria-label="Expand composer"
              aria-expanded={false}
              // Accent-green chevron-up + tinted hover so it reads as
              // interactive. Pre-2026-05-12 this was `text-text-dim` and users
              // missed it.
              className="shrink-0 w-6 h-6 flex items-center justify-center text-accent hover:bg-accent/15 rounded-[2px] transition-colors cursor-pointer"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                className="rotate-180"
                aria-hidden="true"
              >
                {/* chevron-up (rotate-180 of the down glyph) = "expand". */}
                <path
                  d="M2 4 L6 8 L10 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        {/* === Always-present suggestion chips (public-deal-link-polish WS2).
            Chips render in the slim bar itself (collapsed AND expanded) — not
            only inside the expanded overlay — so the starter questions are the
            primary on-ramp without a click to expand (R-WEB3: NarrativeBar
            `expandedSuggestions` is the single always-present chip owner).
            Hidden when gated — the Unlock CTA takes over (gate×chips rule). === */}
        {!gated && expandedSuggestions && expandedSuggestions.length > 0 && (
          <div
            data-testid="narrative-bar-suggestions"
            className="flex flex-wrap items-center gap-2 px-4 pt-2.5"
          >
            {expandedSuggestions.map((chip) => (
              <button
                key={chip}
                type="button"
                data-testid="narrative-bar-suggestion-chip"
                onClick={() => {
                  // Send first so the wrapper mints currentTurn + pendingHuman
                  // BEFORE the expand re-render paints, then reveal the overlay
                  // (chip clicks bypass handleSend's gate).
                  onSuggestionClick?.(chip);
                  maybeExpandOverlay();
                }}
                disabled={disabled}
                className={CHIP_VARIANT_CLASS[chipVariant]}
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* === Row 2: composer — interaction mode OR normal slim composer.
            table-interaction-surface (2026-06-09): when `interaction` is set,
            the slim composer row is REPLACED by `<InteractionSurface
            density="narrative">`. This is a distinct slot from
            `renderAboveComposer` (sibling-overlay-above, requires expand) —
            this replaces the composer row, not overlays it.
            Normal path: breathing-bordered input + gradient send.
            Always slim (single 30px input row). The expanded state grows
            the overlay ABOVE the bar (via `renderAboveComposer`), NOT the
            composer itself — keeps the chat affordance compact and
            consistent across collapsed/expanded states. Pre-2026-05-12
            the expanded state swapped to a 4-row textarea; that growth
            duplicated the overlay's surface area and added vertical chrome
            that competed with the Signal content underneath. */}
        {interaction ? (
          <InteractionSurface
            request={interaction}
            density="narrative"
            currentInputs={currentInputs}
            onDismiss={onInteractionDismiss ?? ((_: void) => void _)}
            billingReadOnly={interactionBillingReadOnly}
            eligibleInputs={eligibleInputs}
          />
        ) : (
          <div className="flex items-center gap-2 px-4 py-2">
            {/* Input wrapper — breathing border + scanline when idle, solid accent
              border + glow when active (focused or has text). Pre-Phase-5
              redesign 2026-05-12, the wrapper was bg-input-bg border-input-border
              with no animation. */}
            <div
              data-active={isActive ? 'true' : 'false'}
              className={`relative flex-1 flex items-center bg-input-bg rounded-[2px] overflow-hidden border transition-[border-color,box-shadow] duration-300 h-[30px] ${
                isActive
                  ? 'border-accent/55 shadow-[0_0_10px_rgba(0,200,83,0.14)]'
                  : 'border-transparent animate-narrative-border-breathe'
              }`}
            >
              {/* Scanline shimmer — idle-only, 40%-wide accent-tinted band. */}
              {!isActive && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none overflow-hidden rounded-[2px]"
                >
                  <div
                    className="absolute top-0 left-0 w-2/5 h-full animate-narrative-scanline"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent, rgba(0,200,83,0.06), transparent)',
                    }}
                  />
                </div>
              )}
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={promptPlaceholder}
                disabled={disabled}
                data-testid="narrative-bar-input"
                className="relative z-10 flex-1 w-full bg-transparent border-none outline-none px-3 text-sm font-body text-text-bright placeholder:text-text-dim disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            {/* ENTER button — gradient when input has text + animated pulse;
              dim transparent when empty. */}
            <button
              type="button"
              onClick={() => void handleSend()}
              data-testid="narrative-bar-send"
              disabled={disabled || input.trim().length === 0}
              aria-label="Send"
              className={`shrink-0 self-stretch h-[30px] px-3.5 rounded-[2px] text-[10px] font-mono font-bold tracking-[0.5px] transition-all duration-300 cursor-pointer disabled:cursor-not-allowed ${
                !disabled && input.trim().length > 0
                  ? 'text-void bg-[linear-gradient(135deg,_var(--color-accent),_hsl(145_80%_32%))] border-none animate-narrative-ask-pulse'
                  : 'text-accent/30 bg-accent/[0.08] border border-accent/15'
              }`}
            >
              ENTER
            </button>
          </div>
        )}
        {/* === Row 3: system status — persistent footer. Live build + version
            on the right; status dot + label on the left. Telemetry fields
            (latency, queue, db sync, cache) render as em-dashes until they're
            wired by a sister observability feature. */}
        <div
          data-testid="narrative-bar-system-status"
          className="flex items-center justify-between px-4 h-[22px] border-t border-panel-border bg-void overflow-hidden"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span
              aria-hidden="true"
              className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_4px_var(--color-accent)] animate-pulse-status shrink-0"
            />
            <span className="text-[9px] font-mono text-text-muted tracking-[0.3px]">
              System Online
            </span>
            <span className="text-[9px] font-mono text-text-dim">·</span>
            <span className="text-[9px] font-mono text-text-dim">
              Feeds nominal
            </span>
          </div>
          <div className="flex items-center gap-2 text-[9px] font-mono text-text-dim shrink-0">
            <span>AES-256</span>
            <span>·</span>
            <span>v2.10</span>
          </div>
        </div>
      </div>
    );
  }

  // mode === 'full'
  const badgeKey = pathname.startsWith('/deal/') ? '/deal' : pathname;
  const badge = PAGE_BADGES[badgeKey] ?? 'ARCANUM';
  const suggestions = PAGE_SUGGESTIONS[badgeKey] ?? [];
  const placeholder = PAGE_PLACEHOLDERS[badgeKey] ?? 'Ask a question...';

  return (
    <div
      data-testid="narrative-bar"
      data-mode="full"
      data-question-count={questionCount}
      data-share-mode={shareMode}
      role="region"
      aria-label="Narrative bar"
      className="shrink-0 border-t border-panel-border bg-void"
    >
      {/* Narrative row */}
      <div className="flex items-center gap-3 px-6 py-2">
        {/* Left: label + badge + context */}
        <div className="flex items-center gap-2 shrink-0">
          <svg
            width="10"
            height="12"
            viewBox="0 0 10 12"
            className="shrink-0"
            aria-hidden="true"
          >
            <rect
              x="0"
              y="0"
              width="3"
              height="12"
              rx="1.5"
              fill="var(--color-accent)"
            />
            <rect
              x="6"
              y="0"
              width="3"
              height="12"
              rx="1.5"
              fill="var(--color-accent)"
            />
          </svg>
          <span className="text-sm font-mono font-bold text-accent tracking-[1px]">
            NARRATIVE
          </span>
          <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded-[2px] bg-panel-border-hi text-text-bright tracking-[0.5px]">
            {badge}
          </span>
          <span className="text-xs font-mono text-text-dim">
            &middot; Oak Grove Apartments
          </span>
        </div>

        {/* Suggestion chips */}
        <div className="flex items-center gap-1.5 flex-1 overflow-hidden">
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="shrink-0 px-3.5 py-2 bg-input-bg border border-input-border rounded-[2px] text-xs font-mono text-text-muted whitespace-nowrap cursor-pointer hover:text-text-bright hover:border-panel-border-hi transition-colors duration-200"
            >
              {s}
            </button>
          ))}
        </div>

        {/* Input + send — hidden on Builder workbench (has its own chat
            input). Builder Signal/Model tabs use mode='prompt' instead so
            this branch never fires for them. */}
        {pathname !== '/builder' && (
          <div className="flex items-center gap-2 flex-1 max-w-sm">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              placeholder={placeholder}
              data-testid="narrative-bar-input"
              className="flex-1 py-2 px-3 bg-input-bg border border-input-border rounded-[2px] text-sm font-mono text-text-bright outline-none placeholder:text-text-dim focus:border-accent/20"
            />
            <button
              type="button"
              onClick={() => void handleSend()}
              data-testid="narrative-bar-send"
              disabled={input.trim().length === 0}
              className="w-8 h-8 flex items-center justify-center bg-input-bg border border-input-border rounded-[2px] text-text-dim hover:text-text-bright hover:border-panel-border-hi cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200 shrink-0"
              aria-label="Send"
            >
              <span className="text-sm font-mono">&#9654;K</span>
            </button>
          </div>
        )}

        {/* Expand toggle */}
        <button
          type="button"
          className="w-6 h-6 flex items-center justify-center text-text-dim hover:text-text-muted cursor-pointer bg-transparent border-none shrink-0"
          aria-label="Expand"
        >
          <span className="text-sm">&#9650;</span>
        </button>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between px-6 py-1.5 border-t border-panel-border bg-void overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shadow-[0_0_4px_var(--color-accent)] animate-pulse-status" />
          <span className="text-xs font-mono text-text-muted">
            System Online
          </span>
        </div>
      </div>
    </div>
  );
}
